import { Button, Dialog, FormField, Tabs } from '../components/ui';
import { useUI } from '../context/UIContext';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api, apiError } from '../api/client';
import { useAuth } from '../context/AuthContext';
import MonitoringPanel from '../components/admin/MonitoringPanel';
import ConnectionsPanel from '../components/admin/ConnectionsPanel';
import ModelUsagePanel from '../components/admin/ModelUsagePanel';
import LogsPanel from '../components/admin/LogsPanel';
import TokenQuotaCell from '../components/admin/TokenQuotaCell';
import OversightPanel, { UserModerationControl } from '../components/admin/OversightPanel';
import { useCapabilities } from '../hooks/useCapabilities';

function PasswordModal({ user, onClose, onSaved }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Пароли не совпадают'); return; }
    setSaving(true);
    setError('');
    try {
      await api.changeUserPassword(user.id, password);
      onSaved();
      onClose();
    } catch {
      setError('Не удалось сменить пароль');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog title={`Смена пароля — ${user.display_name}`} onClose={onClose}>
        <form onSubmit={handleSubmit}>
          <FormField label={<>Новый пароль</>}>
          <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} minLength={6} required autoFocus />
          </FormField>
          <FormField label={<>Подтверждение</>}>
          <input className="form-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required />
          </FormField>
          {error && <p style={{ color: 'var(--danger)', marginBottom: 12, fontSize: '0.9rem' }}>{error}</p>}
          <div className="flex gap-2">
            <Button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button className="btn" type="button" onClick={onClose}>Отмена</Button>
          </div>
        </form>
    </Dialog>
  );
}

function CreateUserForm({ onCreated }) {
  const [form, setForm] = useState({ email: '', display_name: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const created = await api.createUser(form);
      setForm({ email: '', display_name: '', password: '' });
      onCreated(created);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Ошибка создания пользователя');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <h2 className="card-title" style={{ marginBottom: 16 }}>Создать пользователя</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <FormField label={<>Email</>}>
            <input className="form-input" type="email" value={form.email} onChange={e => setForm({
                ...form,
                email: e.target.value
              })} required />
            </FormField>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <FormField label={<>Имя</>}>
            <input className="form-input" value={form.display_name} onChange={e => setForm({
                ...form,
                display_name: e.target.value
              })} required />
            </FormField>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <FormField label={<>Пароль</>}>
            <input className="form-input" type="password" value={form.password} onChange={e => setForm({
                ...form,
                password: e.target.value
              })} minLength={6} required />
            </FormField>
          </div>
          <Button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? '...' : 'Создать'}
          </Button>
        </div>
        {error && <p style={{ color: 'var(--danger)', marginTop: 8, fontSize: '0.9rem' }}>{error}</p>}
      </form>
    </div>
  );
}

export default function AdminPage() {
  const { confirm, notify } = useUI();
  const capabilities = useCapabilities();
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [quotas, setQuotas] = useState({});
  const [loading, setLoading] = useState(true);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.listUsers(), api.listTokenQuotas()])
      .then(([rows, limits]) => { setUsers(rows); setQuotas(Object.fromEntries(limits.map(q => [q.user_id, q]))); })
      .catch(error => notify(apiError(error)))
      .finally(() => setLoading(false));
  }, [notify]);

  useEffect(() => { if (user?.is_admin) load(); }, [load, user?.is_admin]);

  const showMsg = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDelete = async (u) => {
    if (!await confirm(`Удалить пользователя «${u.display_name}»?`)) return;
    setDeleting(u.id);
    try {
      await api.deleteUser(u.id);
      setUsers(prev => prev.filter(x => x.id !== u.id));
    } catch (error) {
      notify(apiError(error));
    } finally {
      setDeleting(null);
    }
  };

  if (!user?.is_admin) return <Navigate to="/" replace />;

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: 24 }}>Администрирование</h1>

      <Tabs label="Администрирование" value={tab} onChange={setTab} items={[
        { key: 'users', label: 'Пользователи' }, { key: 'monitoring', label: 'Мониторинг' },
        { key: 'projects', label: 'Проекты' }, { key: 'sessions', label: 'Сессии' },
        { key: 'security', label: 'Безопасность' },
        { key: 'connections', label: capabilities.acp ? 'Модели и ACP' : 'Модели' },
        { key: 'usage', label: 'Расходы моделей' }, { key: 'logs', label: 'Логи' },
      ]} />

      {['projects', 'sessions', 'security'].includes(tab) ? <OversightPanel key={tab} section={tab} /> : tab === 'usage' ? <ModelUsagePanel /> : tab === 'logs' ? <LogsPanel /> : tab === 'connections' ? (
        <ConnectionsPanel />
      ) : tab === 'monitoring' ? (
        <MonitoringPanel />
      ) : (
        <>
          <CreateUserForm onCreated={(u) => { load(); showMsg(`Пользователь ${u.display_name} создан`); }} />

          {message && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 16,
              background: 'var(--success-bg)', color: 'var(--success)',
              border: '1px solid var(--success)', fontSize: '0.9rem',
            }}>
              {message}
            </div>
          )}

          {loading ? (
            <div className="loading">Загрузка...</div>
          ) : (
            <div className="card table-scroll" style={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Имя', 'Email', 'Роль', 'Статус', 'Telegram', 'Лимит токенов', 'Действия'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 500 }}>{u.display_name}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>{u.email}</td>
                      <td style={{ padding: '10px 16px' }}>
                        {u.is_admin
                          ? <span className="badge badge-running">Администратор</span>
                          : <span className="badge badge-created">Пользователь</span>}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        {u.is_active
                          ? <span className="badge badge-completed">Активен</span>
                          : <span className="badge badge-cancelled">Неактивен</span>}
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        {u.telegram_bot_token ? '✓ подключён' : '—'}
                      </td>
                      <td style={{ padding: '10px 16px' }}><TokenQuotaCell user={u} quota={quotas[u.id]} onChanged={load} /></td>
                      <td style={{ padding: '10px 16px' }}>
                        <div className="flex gap-2">
                          <UserModerationControl user={u} onChanged={load} />
                          <Button
                            className="btn"
                            style={{ padding: '3px 10px', fontSize: '0.82rem' }}
                            onClick={() => setPasswordTarget(u)}
                          >
                            Пароль
                          </Button>
                          {u.id !== user.id && (
                            <Button
                              className="btn"
                              style={{ padding: '3px 10px', fontSize: '0.82rem', color: 'var(--danger)', border: '1px solid currentColor' }}
                              disabled={deleting === u.id}
                              onClick={() => handleDelete(u)}
                            >
                              {deleting === u.id ? '...' : 'Удалить'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {passwordTarget && (
            <PasswordModal
              user={passwordTarget}
              onClose={() => setPasswordTarget(null)}
              onSaved={() => showMsg(`Пароль пользователя ${passwordTarget.display_name} изменён`)}
            />
          )}
        </>
      )}
    </div>
  );
}
